const React = require('react');
const T = require('prop-types');
const { useState, useEffect } = React;
const { context } = require('app-context');

const styledModule = require('styled-components');
const styled = styledModule.default || styledModule;
const { MAX_CONTENT_WIDTH } = require('utils/constants');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: Chip } = require('@mui/material/Chip');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: Table } = require('@mui/material/Table');
const { default: TableBody } = require('@mui/material/TableBody');
const { default: TableCell } = require('@mui/material/TableCell');
const { default: TableHead } = require('@mui/material/TableHead');
const { default: TableRow } = require('@mui/material/TableRow');
const { default: Paper } = require('@mui/material/Paper');
const { default: TableContainer } = require('@mui/material/TableContainer');
const { default: AddIcon } = require('@mui/icons-material/Add');
const { default: EditIcon } = require('@mui/icons-material/Edit');
const { default: BlockIcon } = require('@mui/icons-material/Block');
const FormConfig = require('./formConfig');

const api = context.api.nearpeer;

const FullWidthWrapper = styled.div`
    position: absolute;
    min-width: 100vw;
    left: calc(((100vw - ${MAX_CONTENT_WIDTH}px) / 2) * -1);
    top: 0;
    background-color: white;
    min-height: 100%;

    @media (max-width: ${MAX_CONTENT_WIDTH}px) {
        left: 0;
        width: 100%;
    }
`;

const STATUS_COLORS = {
    approved: 'success',
    draft: 'default',
    pending: 'warning',
    rejected: 'error',
    disabled: 'error'
};

module.exports = function AdminResourcesListPage(props) {

    const { history } = props;

    const [resources, setResources] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {

        api.resources.adminList()
            .then(({ entities, result: ids }) => {

                const list = (ids || []).map((id) => entities.resources[id]).filter(Boolean);
                setResources(list);
            })
            .catch((err) => setError(err.message || 'Failed to load resources'));
    }, []);

    const handleDisable = async (id) => {

        if (!window.confirm('Disable this resource?')) {
            return;
        }

        await api.resources.adminDelete(id);
        setResources((prev) => prev.map((resource) => resource.id === id ? { ...resource, status: 'disabled' } : resource));
    };

    if (error) {
        return (
            <FullWidthWrapper>
                <Box sx={{ p: 3 }}>
                    <Typography color='error'>{error}</Typography>
                </Box>
            </FullWidthWrapper>
        );
    }

    if (resources === null) {
        return (
            <FullWidthWrapper>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </FullWidthWrapper>
        );
    }

    return (
        <FullWidthWrapper>
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>Manage Resources</Typography>
                <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={() => history.push('/app/admin/resources/create')}
                >
                    New Resource
                </Button>
            </Box>
            <TableContainer component={Paper} elevation={1}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Brand</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Priority</TableCell>
                            <TableCell>Targets</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell align='right'>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resources.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8}>
                                    <Typography color='text.secondary' sx={{ textAlign: 'center', py: 2 }}>
                                        No resources yet. Click "New Resource" to create one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {resources.map((resource) => (
                            <TableRow key={resource.id} hover>
                                <TableCell>{resource.brandName}</TableCell>
                                <TableCell>{resource.title}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={resource.status}
                                        color={STATUS_COLORS[resource.status] || 'default'}
                                        size='small'
                                    />
                                </TableCell>
                                <TableCell>{resource.priority}</TableCell>
                                <TableCell>{
                                    (resource.targets || []).some((t) => t.targetType === 'schoolRole' && String(t.targetValue).startsWith('all:'))
                                        ? 'all'
                                        : (resource.targets ? resource.targets.length : 0)
                                }</TableCell>
                                <TableCell>{resource.startDate ? new Date(resource.startDate).toLocaleDateString() : '—'}</TableCell>
                                <TableCell>{resource.endDate ? new Date(resource.endDate).toLocaleDateString() : '—'}</TableCell>
                                <TableCell align='right'>
                                    <IconButton
                                        size='small'
                                        onClick={() => history.push(FormConfig.getResourceEditPath(resource))}
                                        aria-label='Edit resource'
                                    >
                                        <EditIcon fontSize='small' />
                                    </IconButton>
                                    {resource.status !== 'disabled' && (
                                        <IconButton
                                            size='small'
                                            onClick={() => handleDisable(resource.id)}
                                            aria-label='Disable resource'
                                            color='error'
                                        >
                                            <BlockIcon fontSize='small' />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
        </FullWidthWrapper>
    );
};

module.exports.propTypes = {
    history: T.object
};
